import React from 'react'
import { v4 as uuidv4 } from 'uuid'


export const isFlutterApp = () => {
  if (localStorage.getItem('flutterSimulation') === 'true') {
    return true
  }
  return typeof window?.AppBridge !== 'undefined'
}
export const isIOSApp = () => {
  return window?.isIOSApp
}
export const isAndroidApp = () => {
  return window?.isAndroidApp
}

export const getAppVersion = () => {
  return new Promise((resolve) => {
    const get_version_callback = (parsed: { app_version: string, bridge_version: string }) => {
      resolve(parsed)
    }
    MessageToFlutter({
      action: FLUTTER_ACTIONS.GET_VERSION,
      data: {},
      callbackFunction: get_version_callback,
    })
  })
}

// Native builds its reply as `window.${callbackFunction}('<json>')` and runs
// that string verbatim in the WebView, so it has no way to skip a reply for a
// fire-and-forget action: an empty callbackFunction emits `window.('<json>')`,
// which the iOS WKWebView rejects as a SyntaxError ("Unexpected token '('.
// Expected a property name after '.'."). Hand native the name of a real no-op
// instead — that keeps every already-installed app version quiet, which a
// native-side guard alone could not do.
export const FLUTTER_NOOP_CALLBACK = 'flutter_noop_callback'

const registerNoopCallback = () => {
  if (typeof (window as any)[FLUTTER_NOOP_CALLBACK] === 'function') {
    return
  }
  ;(window as any)[FLUTTER_NOOP_CALLBACK] = () => undefined
}

export const MessageToFlutter = (object: {
  action: (typeof FLUTTER_ACTIONS)[keyof typeof FLUTTER_ACTIONS]
  data: {
    [key: string]: number | string | boolean | number[]
  }
  callbackFunction?: (data: any) => void
  permanentFunctionName?: string // for use of notification listener
}) => {
  try {
    if (isFlutterApp()) {
      let messageObject = { ...object }
      registerNoopCallback()
      let functionName = FLUTTER_NOOP_CALLBACK

      if (typeof object?.callbackFunction === 'function') {
        functionName = `flutter_callback_` + uuidv4().replace(/-/g, '_')
        // @ts-ignore
        window[functionName] = (data: string) => {
          const parsed = JSON.parse(data)
          object.callbackFunction?.(parsed)
          if (!object?.permanentFunctionName) {
            delete (window as any)[functionName]
          }
        }
      } else if (object?.permanentFunctionName) {
        functionName = object?.permanentFunctionName
      }
      messageObject.callbackFunction = functionName as any
      const json = JSON.stringify(messageObject)
      window?.AppBridge?.postMessage?.(json)
    }
  } catch (error) {
    window.console.error('MessageToFlutter', error)
  }
}

export const MessageToWeb = (json: string) => {
  if (isFlutterApp()) {
    return JSON.parse(json)
  }
}


export const FLUTTER_ACTIONS = {
  // Google Sign-In authentication - data: {}, callbackFunction: string, returns: {idt: string}
  GOOGLE_LOGIN: 'google_login',

  // Apple Sign-In authentication - data: {}, callbackFunction: string, returns: {idt: string}
  APPLE_LOGIN: 'apple_login',

  // Get Firebase Cloud Messaging token - data: {}, callbackFunction: string, returns: {fcm_token: string}
  GET_FCM_TOKEN: 'get_fcm_token',

  // Pick files from device storage - data: {accept?: string, multiple?: boolean}, callbackFunction: string
  PICK_FILE: 'pick_file',

  // Update app theme color - data: {hex_color: string}, no callback response
  UPDATE_THEME_COLOR: 'update_theme_color',

  // Get current platform information - data: {}, callbackFunction: string, returns: {platform: string}
  GET_PLATFORM: 'get_platform',

  // Sign out user and clear FCM token - data: {}, no callback response
  SIGN_OUT: 'sign_out',

  // Get app version information - data: {}, callbackFunction: string, returns: {app_version: string, bridge_version: string}
  GET_VERSION: 'get_version',

  // Start audio recording - data: {}, callbackFunction: string, returns: {status: "recording"}
  RECORD_AUDIO: 'record_audio',

  // Stop audio recording and get recorded audio - data: {}, callbackFunction: string, returns: {status: "stopped", audio: string}
  STOP_RECORDING: 'stop_recording',

  // Abort current audio recording - data: {}, no callback response
  ABORT_RECORDING: 'abort_recording',

  // Trigger device vibration - data: {durationInMilliSeconds?: number, amplitude?: number, pattern?: number[], intensities?: number[], repeat?: number}, callbackFunction: string, returns: {status: "vibrated", ...}
  VIBRATE: 'vibrate',

  // Download file from URL to device - data: {url: string, filename?: string}, callbackFunction: string, returns: {status: "downloaded" | "error", url: string, filename: string, error?: string, supported: boolean}
  DOWNLOAD_FILE: 'download_file',

  // Get list of supported bridge actions - data: {}, callbackFunction: string, returns: {actions: string[], supported: boolean}
  GET_SUPPORTED_ACTIONS: 'get_supported_actions',

  OPEN_URL: 'open_url',
  // Open URL in browser - data: {url: string}, no callback response

  // Register a permanent listener for incoming voice-call push events pushed by
  // native (FCM). data: {}, permanentFunctionName: string. Native invokes the
  // listener with a JSON call_update payload: {event_name:'call_update',
  // call_status, customer_id, sender_id?, sdp_answer?, contact?}
  LISTEN_VOICE_CALLS: 'listen_voice_calls',

  // Inform native of a voice call state change so it can manage the call
  // notification + keep the webview alive (Android foreground service).
  // data: {status: 'connected'|'on_hold'|'ended', customer_id: string}, no callback
  VOICE_CALL_STATE: 'voice_call_state',

  // ---- Bridge v3: native call layer (webkanda lib/call) ----------------------
  // Register the permanent native→web call-event emitter.
  // permanentFunctionName: NATIVE_CALL_LISTENER
  REGISTER_NATIVE_CALL_LISTENER: 'register_native_call_listener',

  // Show the native dialing screen immediately, before call_request/media work.
  // data: {customer_id, customer_name?, phone?}
  NATIVE_CALL_PREPARE: 'native_call_prepare',

  // Ask native to build the outgoing peer + bridge-ctrl DataChannel and return
  // the raw SDP offer. data: {customer_id, customer_name?, phone?,
  // ice_servers: JSON-stringified RTCIceServer[]} → cb {status:'ok', sdp} |
  // {status:'error', code}
  NATIVE_CALL_START_OUTGOING: 'native_call_start_outgoing',

  // Bind the backend call_id once agent_sdp succeeded.
  // data: {customer_id, call_id, control_url?}
  NATIVE_CALL_BIND: 'native_call_bind',

  // Relay the MQTT sdp_answer down to native (raw SDP string).
  // data: {customer_id, call_id, sdp}
  NATIVE_CALL_REMOTE_ANSWER: 'native_call_remote_answer',

  // Relay a normalized status downlink. data: {event: JSON string
  // {v:1, customer_id, call_id, status, direction, sender_id}}
  NATIVE_CALL_EVENT: 'native_call_event',

  // Drive a native call. data: {action: 'hangup'|'hold'|'resume'|'mute'|'unmute'|
  // 'cancel_dialing', customer_id}
  NATIVE_CALL_ACTION: 'native_call_action',

  // Snapshot native call state. cb: {calls: [...], active_customer_id}
  GET_NATIVE_CALL_STATE: 'get_native_call_state',

  // iOS PushKit VoIP token (null until app M4). cb: {platform, voip_token}
  GET_VOIP_TOKEN: 'get_voip_token',
}

// Permanent window function native calls to push incoming voice-call events.
export const VOICE_CALL_LISTENER = 'flutter_permanent_callback_voice_call'

/**
 * Register a permanent listener so native can push incoming-call payloads into
 * the webview. Returns an unregister function. No-op outside the Flutter app.
 */
export const registerVoiceCallListener = (
  handler: (payload: unknown) => void
): (() => void) => {
  if (!isFlutterApp()) {
    return () => undefined
  }
  window[VOICE_CALL_LISTENER] = (data: string) => {
    try {
      handler(JSON.parse(data))
    } catch (error) {
      window.console.error('voiceCallListener', error)
    }
  }
  MessageToFlutter({
    action: FLUTTER_ACTIONS.LISTEN_VOICE_CALLS,
    data: {},
    permanentFunctionName: VOICE_CALL_LISTENER,
  })
  return () => {
    delete window[VOICE_CALL_LISTENER]
  }
}

// ---- Native call mode (bridge v3) -------------------------------------------

// Minimum bridge version whose native side implements the call layer.
export const NATIVE_CALL_MIN_BRIDGE_VERSION = 3

// Per-device kill lever, default ON whenever the app is capable (bridge v3 +
// native_call_support). Setting this to 'false' opts a device OUT of native
// outgoing calling (falls back to browser WebRTC). Replaced by the
// store-level `native_calling_enabled` backend flag in M2. NOTE: incoming
// calls always ring via the browser path regardless of this flag — native
// incoming lands in M3.
export const NATIVE_CALL_DEV_FLAG = 'nativeCallMode'

// Permanent window function native calls with call events (JSON string arg).
export const NATIVE_CALL_LISTENER = 'flutter_native_call_event'

export interface NativeCallEvent {
  type: 'state' | 'signal_request' | 'answered_native' | 'recents_dial'
  customer_id: string
  call_id?: string
  /** For type 'state': ringing|dialing|connecting|ongoing|on_hold|ended */
  state?: string
  reason?: string
  direction?: string
  muted?: boolean
  started_at?: number
  /** For type 'signal_request': the REST action native asks the web to run. */
  request?: 'reject' | 'hangup'
}

/**
 * Native-call relay mode: the app draws the call UI and owns media; the web
 * relays signaling. Activates automatically whenever the app is capable
 * (bridge v3 + native_call_support) — no opt-in required. The dev flag is an
 * opt-out kill lever: setting it to 'false' falls back to browser WebRTC on
 * that device.
 */
export const isNativeCallMode = (): boolean =>
  isFlutterApp() &&
  Number((window as any)?.native_bridge_version ?? 0) >=
    NATIVE_CALL_MIN_BRIDGE_VERSION &&
  (window as any)?.native_call_support === true &&
  localStorage.getItem(NATIVE_CALL_DEV_FLAG) !== 'false'

/**
 * Register the permanent listener native pushes call events through.
 * Returns an unregister function. No-op outside the Flutter app.
 */
export const registerNativeCallListener = (
  handler: (event: NativeCallEvent) => void
): (() => void) => {
  if (!isFlutterApp()) {
    return () => undefined
  }
  ;(window as any)[NATIVE_CALL_LISTENER] = (data: string) => {
    try {
      handler(JSON.parse(data) as NativeCallEvent)
    } catch (error) {
      window.console.error('nativeCallListener', error)
    }
  }
  MessageToFlutter({
    action: FLUTTER_ACTIONS.REGISTER_NATIVE_CALL_LISTENER,
    data: {},
    permanentFunctionName: NATIVE_CALL_LISTENER,
  })
  return () => {
    delete (window as any)[NATIVE_CALL_LISTENER]
  }
}

// App lifecycle states native (Flutter) pushes as it foregrounds/backgrounds.
export const APP_STATE = {
  FOREGROUND: 'foreground',
  BACKGROUND: 'background',
} as const

// Permanent window function native calls on every app foreground/background
// transition: window.native_app_state('{"state":"foreground"}').
export const NATIVE_APP_STATE_LISTENER = 'native_app_state'

/**
 * Register a listener for native app foreground/background transitions. Native
 * (Flutter) invokes window.native_app_state with a JSON string {state}. Returns
 * an unregister function. No-op outside the Flutter app.
 */
export const registerAppStateListener = (
  handler: (state: string) => void
): (() => void) => {
  if (!isFlutterApp()) {
    return () => undefined
  }
  window[NATIVE_APP_STATE_LISTENER] = (data: string) => {
    try {
      const parsed = JSON.parse(data) as { state?: string }
      if (parsed?.state) {
        handler(parsed.state)
      }
    } catch (error) {
      window.console.error('appStateListener', error)
    }
  }
  return () => {
    delete window[NATIVE_APP_STATE_LISTENER]
  }
}

/** Tell native the call's state changed (notification + keep-alive). No-op on web. */
export const notifyVoiceCallState = (
  status: string,
  customerId: string
): void => {
  MessageToFlutter({
    action: FLUTTER_ACTIONS.VOICE_CALL_STATE,
    data: { status, customer_id: customerId },
  })
}

export const openUrlInDefaultBrowser = (url: string, e?: React.MouseEvent<HTMLAnchorElement>) => {
  if (isFlutterApp() && url?.length) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    MessageToFlutter({
      action: FLUTTER_ACTIONS.OPEN_URL,
      data: {
        url,
      },
    })
    return true
  }
  return false
}

export const openUrlWrapperForFlutterApp = (e: React.MouseEvent<HTMLAnchorElement>) => {
  const url = (e?.currentTarget as HTMLAnchorElement)?.href as string
  if (url?.length) {
    return openUrlInDefaultBrowser(url, e)
  }
  return false
}